<script setup>
import { useBetsStore } from '@/stores/bets';
import { storeToRefs } from 'pinia';
import { computed, ref, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import { useBetslipStore } from '@/stores/betslip';
import { useResponseMessageStore } from '@/stores/response_message';
import { useFixturesStore } from '@/stores/fixtures';
import { timestampToDayMonth, timestampToTime, toFixedTwo, timestampToDayMonthString, safeTranslate } from '@/helpers';
import MyBetFooterAmount from './MyBetFooterAmount.vue';
import MyBetTimeline from './MyBetTimeline.vue';
import MyBetCashout from './MyBetCashout.vue';
import PrintTicket from './PrintTicket.vue'
import { formatMarketName } from '@/helpers'

const props = defineProps({
    ticket: Object,
})

const emit = defineEmits(['cashout'])
const { t, te } = useI18n()
const auth = useAuthStore()
const bets = useBetsStore()
const betslip = useBetslipStore()
const fixtures = useFixturesStore()
const responseMessageStore = useResponseMessageStore()

const { getCurrency, getUser } = storeToRefs(auth)
const { getEvent } = storeToRefs(fixtures)
const isOpen = computed(() => props.ticket.st == 6 && getUser.value.betop && getUser.value.rl == "clt")
const timeline = ref(0)
const timelineSlip = ref(0)
const collapsed = ref(!isOpen.value)
const betslips = ref([])
const date = computed(() => timestampToDayMonth(props.ticket.createdAt))
const areSelectionsWaiting = computed(() => betslips.value.every((b) => b.st != 1))
const printTicketRef = ref(null);

const marketName = (slip) => {
  const market = {
    i: slip.mfid,          
    n: slip.mnm,            
    nrs: slip.mnrs || []   
  }
  const event = {
    h: slip.ht,
    a: slip.at
  }

  return formatMarketName(market, event)
}
const selectionTranslation = (selection) => { 
    return safeTranslate('selections', selection.oddnm, t, te)
}
const openTimeline = (eid, oddid) => {
    if (timeline.value == eid && timelineSlip.value == oddid) {
        timeline.value = 0
        timelineSlip.value = 0
    } else {
        timeline.value = eid
        timelineSlip.value = oddid
    }
}
const fetchSlips = async () => {
    if (collapsed.value) {
        await bets.fetchBetslips(props.ticket._id).then(res => {
            if (res.data.ok) {
                betslips.value = res.data.data
                collapsed.value = false
            }
        })
    } else {
        collapsed.value = true
    }
}

onMounted(() => {
  if (!collapsed.value ) {
    bets.fetchBetslips(props.ticket._id).then(res => {
      if (res.data.ok) {
        betslips.value = res.data.data;
      }
    });
  }
});
const openPrintModal = async (ticketNr)=>{
    if(collapsed.value ){
        await bets.fetchBetslips(props.ticket._id).then(res => {
            if (res.data.ok) {
                betslips.value = res.data.data
            }
        })
    }
    printTicketRef.value.openModal();
}

const getSelection = async(slip , isl)=>{
    const selections = await fixtures.fetchEventSelection(slip.eid , isl)
    let m = selections[0].m.find(s=> s.i == slip.mfid)
    let o = m.o.find(o=>o.i == slip.oddid) || nul
    return o 
}

async function findOdd(slip) {
    if(getEvent.value(slip.eid)){
        if (getEvent.value(slip.eid).s == 1) {
            return await getSelection(slip , 1);
        }
        if (getEvent.value(slip.eid).s == 0) {
            return await getSelection(slip, 0);
        }
    }
}

const repeat = async (ticket) => {
  betslip.clear();

  const res = await bets.fetchBetslips(ticket._id);
  if (!res.data.ok) return;

  let added = 0;
  for (const slip of res.data.data) {
    const odd = await findOdd(slip);
    if (odd && odd.st != 3) {
      betslip.setOdd(odd , slip.eid, slip.mfid);
      added++;
    }
  }

  if (added == 0) {
    responseMessageStore.setMessage(t('ticket.notRepeated'));
  }
};
</script>

<template>
    <div class="myb-Ticket">
        <div class="myb-TicketHeader" @click="fetchSlips">
            <div class="myb-TicketHeaderNr">
                <p>
                    <template v-if="getUser.rl == 'clt'">{{ ticket.nr }}</template>
                    <template v-else>{{ ticket.unm }}</template>
                </p>
                <p class="text-xs font-normal text-neutral-400" >
                    {{ date[0] }} {{ date[1] }} {{ date[2] }}
                    {{ timestampToTime(ticket.createdAt) }}
                </p>
            </div>
            <div class="myb-TicketHeaderAct">
                <div v-if="ticket.st == 6"> 
                    <button 
                        class="text-center w-full inline-flex text-xs text-emerald-600 border border-emerald-400 px-1.5 py-1 cursor-pointer"
                        @click.stop="repeat(ticket)"
                    >
                        {{t('ticket.repeat')}}
                    </button>
                </div>
                <div
                    v-if="ticket.st != 6"
                    :class="[
                        'myb-TicketStatus',
                        {'win': ticket.st == 2},
                        {'lost': ticket.st == 1}
                    ]"
                >
                    <span class="mr-1.5">{{ t('ticket.' + ticket.st) }}</span>
                    <span v-if="ticket.st == 2"><span v-if="ticket.is_c_out">Cashout&emsp14;</span>{{ ticket.finp }}</span>
                    <span v-if="ticket.st == 1">{{ ticket.am }}</span>
                    <span class="ml-0.5" v-if="ticket.st == 2 || ticket.st == 1">{{ getCurrency(ticket.cuid).cod }}</span>
                </div>

                <div
                    class="myb-TicketCancel"
                    v-if="ticket.st == 6"
                    @click.stop="bets.cancelTicket(ticket._id)"
                >{{$t('partials.7')}}</div>
                <button class="flex items-center justify-end" @click.stop="openPrintModal(ticket.nr)" >
                    <img src="/print.svg" class="w-5 h-5"/>
                </button>
            </div>
        </div>
        <div v-if="!collapsed" class="myb-TicketBetslips">
            <ul class="list-none space-y-5">
                <li v-for="slip in betslips">
                    <div class="myb-TicketBetslip">
                        <div class="flex-shrink-0 mt-1 mr-1.5">
                            <img src="/win.svg" class="w-3 h-3" v-if="slip.st == 2" />
                            <img src="/lost.svg" class="w-3 h-3" v-if="slip.st == 1" />
                            <div class="w-3 h-3 bg-neutral-400 rounded-full" v-if="slip.st != 2 && slip.st != 1" />
                        </div>
                        <div class="flex-1 cursor-pointer" @click="openTimeline(slip.eid, slip.oddid)">
                            <div class="text-neutral-400 text-xs" v-if="slip.tm">
                                <p>{{timestampToDayMonthString(slip.tm)}} {{ timestampToTime(slip.tm)}}</p>
                            </div>
                            <p class="text-neutral-200 font-bold text-sm relative myb-SlipTeams ">
                                <span v-if="slip.isl">Live - </span>
                                <span>
                                    <span v-if="!slip.isOutright">{{ slip.ht }} v {{ slip.at }}</span>
                                    <span v-else>{{ slip.outrightName }}</span>
                                </span>
                            </p>
                            <div class="mt-1 flex justify-between">
                                <p class="text-neutral-400 text-xs">
                                    <span>{{ marketName( slip, slip.ht , slip.at)  }}</span>
                                    ({{ selectionTranslation(slip) }})
                                </p>
                                <p class="text-neutral-300 text-xs">{{ toFixedTwo(slip.p) }}</p>
                            </div>

                            <template v-if="timeline == slip.eid && timelineSlip == slip.oddid">
                                <MyBetTimeline
                                    :eid="timeline"
                                    :sid="slip.sid"
                                />
                            </template>
                        </div>
                    </div>
                </li>
            </ul>
        </div>
        <template v-if="!collapsed">
            <div class="border-t border-neutral-500/30">
                <div class="grid grid-cols-6 px-4 py-3">
                    <div class="col-span-2">
                        <MyBetFooterAmount
                            class="col-span-2" 
                            label="Stake"
                            :value="(ticket?.tx && ticket?.amat) ? ticket.amat : ticket.am"
                            :currency="getCurrency(ticket.cuid).cod"
                        />
                        <p class="text-xs text-white" v-if="ticket?.tx && ticket?.amat">( {{ toFixedTwo (ticket.am - ticket.amat) }} Tax )</p>
                    </div>
                    <MyBetFooterAmount
                        class="col-span-2"
                        label="Total Return"
                        :value="ticket.mx"
                        :currency="getCurrency(ticket.cuid).cod"
                        v-if="ticket.tp != 2"
                    />
                    <MyBetFooterAmount
                        class="col-span-2"
                        :label="'Bonus('+ ticket.b_value + '%)'"
                        :value="toFixedTwo(ticket?.bn)"
                        :currency="getCurrency(ticket.cuid).cod"
                        v-if="ticket.b_value"
                    />
                </div>
                <div class="grid grid-cols-3 px-4 py-3 border-t border-neutral-500/30" v-if="ticket.tp == 2">
                    <MyBetFooterAmount
                        label="Min. To Return"
                        :value="ticket.mn"
                        :currency="getCurrency(ticket.cuid).cod"
                    />
                    <MyBetFooterAmount
                        label="Max. To Return"
                        :value="ticket.mx"
                        :currency="getCurrency(ticket.cuid).cod"
                    />
                </div>
            </div>

            <MyBetCashout
                :ticket-id="ticket._id"
                v-if="ticket.tp != 2 && ticket.st == 6 && !ticket.is_c_out && areSelectionsWaiting && getUser.accashout && getUser.rl == 'clt'"
                @accepted="$emit('cashout')"
            />
        </template>
        <PrintTicket ref="printTicketRef" :ticket="ticket" :betslips="betslips" :key="ticket._id"/>
    </div>
</template>
<style scoped>
.myb-Ticket {
    background-image: var(--mgc-bg);
    @apply rounded-md overflow-hidden;
}

.myb-TicketHeader {
    @apply h-14 px-4 flex items-center justify-between cursor-pointer;
}

.myb-TicketStatus {
    @apply text-xs;
}
.myb-TicketStatus.win {
    @apply text-emerald-400
}
.myb-TicketStatus.lost {
    color: red;
}

.myb-TicketHeaderNr {
    @apply font-bold;
    color: var(--color-primary4)
}
.myb-TicketHeaderAct{
    @apply flex items-center gap-2
}
.myb-TicketCancel {
    @apply inline-flex text-xs text-neutral-400 border border-neutral-400 px-1.5 py-1 cursor-pointer;
}

.myb-TicketBetslips {
    @apply border-t border-neutral-500/30 px-4 py-2;
}

.myb-TicketBetslip {
    @apply flex;
}
.myb-SlipTeams{
    width: 100%;
    padding-right: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 1; 
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal; 
    word-break: break-word; 
}
.myb-SlipTeams::after {
    content: '>';
    font: 28px "Consolas", monospace;
    color: #bababa;
    -webkit-transform: rotate(90deg);
    -moz-transform: rotate(90deg);
    -ms-transform: rotate(90deg);
    transform: rotate(90deg);
    right: 0;
    padding: 0 0 0;
    position: absolute;
    pointer-events: none;
    top: -1px;
}
</style>
